import * as React from 'react';

function getLocalStorageItem<T>({ key, initialValue }: { key: string; initialValue: T }): T {
	try {
		const item = window.localStorage.getItem(key);
		if (item) {
			return JSON.parse(item);
		}
	} catch (error) {
		console.warn(`Failed to read localStorage key "${key}":`, error);
	}
	return initialValue;
}

function setLocalStorageItem<T>(key: string, value: T): void {
	try {
		window.localStorage.setItem(key, JSON.stringify(value));
	} catch (error) {
		console.warn(`Failed to write localStorage key "${key}":`, error);
	}
}

type SetValueFunction<T> = (value: T | ((prevValue: T) => T)) => void;

export function useLocalStorage<T>({
	key,
	initialValue,
}: {
	key: string;
	initialValue?: T;
}): [T | undefined, SetValueFunction<T | undefined>] {
	const [storedValue, setStoredValue] = React.useState(() => getLocalStorageItem<T>({ key, initialValue }));
	const [prevKey, setPrevKey] = React.useState(key);
	if (key !== prevKey) {
		setPrevKey(key);
		setStoredValue(() => getLocalStorageItem<T>({ key, initialValue }));
	}
	const setValue = React.useCallback(
		(value: T | ((prevValue: T) => T)) => {
			setStoredValue(prev => {
				const newValue = typeof value === 'function' ? (value as (prevValue: T) => T)(prev) : value;
				setLocalStorageItem(key, newValue);
				return newValue;
			});
		},
		[key]
	);
	return [storedValue, setValue];
}
